import { API_BASE } from '../apiBase';
import BillToPicker from '../lib/BillToPicker';
import { fetchOrgAddressMap, billingAddressFor } from '../lib/orgAddress';
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

function CreateReceipt() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const openId = searchParams.get('id');         // reopen an existing receipt by id
  const openNumber = searchParams.get('number'); // ...or by its receipt number
  const [savedNumber, setSavedNumber] = useState(null); // set once saved/loaded — locks the number, disables re-save

  // State
  const [projectSearch, setProjectSearch] = useState('');
  const [selectedProject, setSelectedProject] = useState(null);
  // WHICH PERSON this receipt is issued to — null = the project's customer.
  const [billToCust, setBillToCust] = useState(null);
  const [orgAddrs, setOrgAddrs] = useState(new Map());
  useEffect(() => { fetchOrgAddressMap().then(setOrgAddrs); }, []);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  // Split payments: one receipt can cover several payments (a check one day,
  // Zelle a few days later) — one row each, summed into the receipt total.
  const todayYMD = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
  const [payRows, setPayRows] = useState([{ date: todayYMD(), amount: '', method: 'Check' }]);
  const updatePayRow = (i, f, v) => setPayRows(rows => rows.map((r, j) => j === i ? { ...r, [f]: v } : r));
  const addPayRow = () => setPayRows(rows => [...rows, { date: todayYMD(), amount: '', method: 'Zelle' }]);
  const removePayRow = (i) => setPayRows(rows => rows.length > 1 ? rows.filter((_, j) => j !== i) : rows);
  const setFirstRowAmount = (amt) => setPayRows(rows => rows.map((r, j) => j === 0 ? { ...r, amount: String(amt ?? '') } : r));
  // Standard methods get a dropdown entry; anything else (Venmo, Cash App,
  // Square, Apple Pay…) is typed free-form via "Other".
  const STD_METHODS = ['Check', 'Cash', 'Zelle', 'Wire Transfer', 'Credit Card'];
  // Card processing fee: 3.5% of the card-paid rows, shown as its own line —
  // the invoice balance math never includes it (the fee pays the processor,
  // not the bill). Declared BEFORE the derived fee math below (TDZ).
  const [showCardFee, setShowCardFee] = useState(false);
  // check-clearance disclaimer is now opt-out via toggle (still needs an actual check row)
  const [showCheckNote, setShowCheckNote] = useState(true);
  const totalPaidNow = payRows.reduce((s, p) => s + Number(p.amount || 0), 0);
  const hasCheck = payRows.some(p => p.method === 'Check' && Number(p.amount) > 0);
  const cardFeeBase = (() => {
    const cardRows = payRows.filter((p) => /card/i.test(p.method || '') && Number(p.amount) > 0);
    const base = cardRows.reduce((s, p) => s + Number(p.amount || 0), 0);
    return base > 0 ? base : totalPaidNow; // no card-labeled row? apply to the whole payment
  })();
  const cardFee = showCardFee ? Math.round(cardFeeBase * 0.035 * 100) / 100 : 0;
  const totalCharged = totalPaidNow + cardFee;
  const methodSummary = [...new Set(payRows.filter(p => Number(p.amount) > 0).map(p => p.method || 'Other'))].join(' / ') || payRows[0].method || 'Other';
  const [memo, setMemo] = useState('');
  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [fetchError, setFetchError] = useState(null);

  const [projectInvoices, setProjectInvoices] = useState([]);
  const [previousPayments, setPreviousPayments] = useState([]); 

  // Visibility Booleans
  const [showInvoiceTotal, setShowInvoiceTotal] = useState(false);
  const [showPrevPayments, setShowPrevPayments] = useState(false);
  const [showRemainingBalance, setShowRemainingBalance] = useState(true);

  const currentYearShort = new Date().getFullYear().toString().slice(-2);

  // Phone layout: stack form + preview, wrap header buttons, tighten padding.
  const mobileQuery = '(max-width: 768px)';
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.matchMedia(mobileQuery).matches);
  useEffect(() => {
    const mq = window.matchMedia(mobileQuery);
    const onChange = (e) => setIsMobile(e.matches);
    mq.addEventListener ? mq.addEventListener('change', onChange) : mq.addListener(onChange);
    return () => { mq.removeEventListener ? mq.removeEventListener('change', onChange) : mq.removeListener(onChange); };
  }, []);

  // Dynamic Receipt Number (a saved/reopened receipt keeps its stored number)
  const receiptNumber = useMemo(() => {
    if (savedNumber) return savedNumber;
    if (!selectedProject?.poNumber) return `RCPT${currentYearShort}-XXXX-001 (select project)`;
    return `RCPT${currentYearShort}_${selectedProject.poNumber}_001`;
  }, [selectedProject, currentYearShort, savedNumber]);

  // Fetch Projects
  useEffect(() => {
    const fetchProjects = async () => {
      const token = localStorage.getItem('token');
      if (!token) return navigate('/login');

      try {
        setLoadingProjects(true);
        const res = await axios.get(`${API_BASE}/projects`, {
          headers: { Authorization: token },
        });
        setProjects(res.data);
      } catch (err) {
        console.error('Failed to load projects:', err);
        setFetchError('Failed to load projects.');
      } finally {
        setLoadingProjects(false);
      }
    };
    fetchProjects();
  }, [navigate]);

  // Fetch Invoices and filter by Selected Project
  useEffect(() => {
    if (!selectedProject?._id) return;
    
    const fetchInvoices = async () => {
      const token = localStorage.getItem('token');
      try {
        const res = await axios.get(`${API_BASE}/invoices`, {
          headers: { Authorization: token }
        });
        
        // Filter based on your populated schema (checking both object ID and string)
        const filtered = res.data.filter(inv => {
          const invProjId = inv.project?._id || inv.project;
          return invProjId === selectedProject._id;
        });
        
        setProjectInvoices(filtered.filter(inv => inv.status !== 'Revised')); // superseded paper can't be receipted
      } catch (err) {
        console.error('Error fetching invoices:', err);
      }
    };
    fetchInvoices();
  }, [selectedProject]);

  // Reopen an existing receipt (?id= or ?number=) once projects have loaded —
  // restores everything so you can review it and hit Send.
  useEffect(() => {
    if ((!openId && !openNumber) || projects.length === 0) return;
    const load = async () => {
      try {
        const token = localStorage.getItem('token');
        const q = openId ? `id=${openId}` : `number=${encodeURIComponent(openNumber)}`;
        const { data: rcpt } = await axios.get(`${API_BASE}/receipts/lookup?${q}`, {
          headers: { Authorization: token },
        });
        const projId = rcpt.project?._id || rcpt.project;
        const proj = projects.find((p) => p._id === projId);
        if (proj) setSelectedProject(proj);
        // Restore split rows when saved with them; older receipts fall back
        // to one lump-sum row.
        const toYMD = (d) => { const x = new Date(d); return isNaN(x) ? todayYMD() : `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`; };
        if (Array.isArray(rcpt.payments) && rcpt.payments.length) {
          setPayRows(rcpt.payments.map(p => ({ date: typeof p.date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(p.date) ? p.date : toYMD(p.date), amount: String(p.amount ?? ''), method: p.method || 'Check' })));
        } else {
          setPayRows([{ date: toYMD(rcpt.date), amount: String(rcpt.amount ?? ''), method: rcpt.method || 'Check' }]);
        }
        setMemo(rcpt.memo || '');
        setPreviousPayments(Array.isArray(rcpt.previousPayments) ? rcpt.previousPayments : []);
        setSavedNumber(rcpt.number || null);
        setShowCardFee(!!rcpt.showCardFee);
        setShowCheckNote(rcpt.showCheckNote !== false);
        if (rcpt.invoice) {
          try {
            const { data: inv } = await axios.get(`${API_BASE}/invoices/${rcpt.invoice?._id || rcpt.invoice}`, {
              headers: { Authorization: token },
            });
            setSelectedInvoice(inv);
          } catch (_) { /* invoice link is optional */ }
        }
      } catch (err) {
        console.error('Load receipt failed:', err);
        alert('Could not load that receipt.');
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openId, openNumber, projects]);

  // Arriving from a project's Revenue tab (?project= and optionally &invoice=)
  // — preselect the project and, when given, the invoice with amount filled.
  const prefillProject = searchParams.get('project');
  const prefillInvoice = searchParams.get('invoice');
  useEffect(() => {
    if (!prefillProject || openId || openNumber || projects.length === 0 || selectedProject) return;
    const proj = projects.find((p) => p._id === prefillProject);
    if (proj) setSelectedProject(proj);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects]);
  useEffect(() => {
    if (!prefillInvoice || openId || openNumber) return;
    axios.get(`${API_BASE}/invoices/${prefillInvoice}`, { headers: { Authorization: localStorage.getItem('token') } })
      .then(({ data: inv }) => { if (inv) { setSelectedInvoice(inv); setFirstRowAmount(inv.total); } })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Calculations
  const invoiceTotal = selectedInvoice?.total || 0;
  const totalPrevPaid = previousPayments.reduce((sum, p) => sum + Number(p.amount || 0), 0);
  const remainingBalance = invoiceTotal - totalPrevPaid - totalPaidNow;

  const formatPrice = (value) => {
    return Number(value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const addPrevPayment = () => setPreviousPayments([...previousPayments, { date: '', amount: 0 }]);
  const updatePrevPayment = (idx, field, val) => {
    const updated = [...previousPayments];
    updated[idx][field] = val;
    setPreviousPayments(updated);
  };
  const removePrevPayment = (idx) => setPreviousPayments(previousPayments.filter((_, i) => i !== idx));

const handleSave = async () => {
  if (!selectedProject) return alert('Please select a project');
  const token = localStorage.getItem('token');

  // MATCHING the new Receipt Schema in your server.js
  const payload = {
    number: receiptNumber,
    project: selectedProject._id,
    invoice: selectedInvoice?._id, // Sending the ObjectId for the audit
    amount: totalPaidNow,
    method: methodSummary,
    payments: payRows.filter(p => Number(p.amount) > 0).map(p => ({ date: p.date, amount: Number(p.amount), method: p.method || 'Other' })),
    showCardFee,
    showCheckNote,
    previousPayments: previousPayments, // Sent to the backend
    memo: memo,
    date: new Date()
  };

  try {
    // CHANGE: Point this to the /receipts endpoint
    await axios.post(`${API_BASE}/receipts`, payload, {
      headers: { Authorization: token }
    });

    // STAY on the page (previously this bounced to the project, making it
    // impossible to hit Send after saving). Lock the number + Save button.
    setSavedNumber(payload.number);
    alert('Receipt saved and payment recorded — you can now Send it.');
  } catch (err) {
    console.error("Save Error:", err.response?.data || err.message);
    alert('Error saving receipt. Check console for details.');
  }
};

  // Builds and returns the jsPDF document (no download) — used by both the
  // Download button and the Outlook-draft Send flow.
  const buildPDF = () => {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    // Same crisp logo the invoices use — LogoXS printed blurry.
    doc.addImage('/media/Logo_hires.png', 'PNG', 165, 10, 25, 25);

    let y = 45;
    doc.setFontSize(11);
    doc.text(`Receipt # ${receiptNumber}`, 15, y); y += 6;
    doc.text(`Date: ${new Date().toLocaleDateString()}`, 15, y); y += 14;

    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('STALWART ELECTRIC', 15, y);
    doc.text('RECEIVED FROM:', 85, y);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    // Company block: address, phone, license — matches the invoice header.
    const companyLines = ['4712 Solano Way', 'Fair Oaks, CA 95628', '916-860-4198', 'CSLB Lic. #1109509 · C-10'];
    doc.text(companyLines, 15, y + 12);
    const rCust = billToCust || selectedProject?.customer || {};
    // Business name + Attn only when customer has a business AND this project
    // bills the business (billTo !== 'Individual').
    const rAddr = billingAddressFor(rCust, orgAddrs);
    const custAddr1 = rAddr.street || '';
    const custAddr2 = [rAddr.city, rAddr.state, rAddr.zip].filter(Boolean).join(', ');
    const receivedFromLines = [
      ...((rCust.businessName && selectedProject?.billTo !== 'Individual')
        ? [rCust.businessName, `Attn: ${rCust.name || ''}`.trim()]
        : [rCust.name || 'Client']),
      custAddr1, custAddr2,
      rCust.primaryPhone || '',
      selectedProject?.title || '',
    ].filter(Boolean);
    doc.text(receivedFromLines, 85, y + 12);
    // Table starts below whichever header column runs longer.
    const headerDepth = Math.max(companyLines.length, receivedFromLines.length) * 5 + 18;

    const bodyRows = [];
    if (showInvoiceTotal) {
      bodyRows.push([`Original Invoice Total (${selectedInvoice?.number || '---'})`, '', `$${formatPrice(invoiceTotal)}`]);
    }
    if (showPrevPayments) {
      previousPayments.forEach(p => {
        bodyRows.push([`Payment Received (${p.date})`, 'Statement Ref', `-$${formatPrice(p.amount)}`]);
      });
    }
    const liveRows = payRows.filter(p => Number(p.amount) > 0);
    if (liveRows.length <= 1) {
      const only = liveRows[0] || payRows[0];
      bodyRows.push([`CURRENT PAYMENT for ${selectedInvoice?.number || 'Project'}`, only.method, `$${formatPrice(only.amount)}`]);
    } else {
      liveRows.forEach(p => {
        bodyRows.push([`PAYMENT RECEIVED (${p.date}) for ${selectedInvoice?.number || 'Project'}`, p.method, `$${formatPrice(p.amount)}`]);
      });
      bodyRows.push(['TOTAL PAYMENTS THIS RECEIPT', '', `$${formatPrice(totalPaidNow)}`]);
    }
    if (showCardFee && cardFee > 0) {
      bodyRows.push(['Card processing fee (3.5%)', '', `$${formatPrice(cardFee)}`]);
    }

    doc.autoTable({
      startY: y + headerDepth,
      head: [['Description', 'Method', 'Amount']],
      body: bodyRows,
      theme: 'grid',
      headStyles: { fillColor: [239, 70, 50] },
      columnStyles: { 2: { halign: 'right' } }
    });

    const finalY = doc.lastAutoTable.finalY + 15;
    doc.setFont('helvetica', 'bold');
    // Charged-amount first; the balance (ideally $0.00) reads LAST.
    let totalsY = finalY;
    if (showCardFee && cardFee > 0) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.setTextColor(90);
      doc.text(`Total charged (incl. card fee): $${formatPrice(totalCharged)}`, 196, totalsY, { align: 'right' });
      doc.setTextColor(0);
      doc.setFont('helvetica', 'bold');
      totalsY += 7;
    }
    if (showRemainingBalance) {
      doc.text(`Total Balance Due: $${formatPrice(remainingBalance)}`, 196, totalsY, { align: 'right' });
    } else {
      doc.text(`Total Received: $${formatPrice(totalPaidNow)}`, 196, totalsY, { align: 'right' });
    }
    // Memo — was captured and saved but never printed on the PDF.
    if (memo && memo.trim()) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.text('Memo', 15, finalY + 12);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(90);
      doc.text(doc.splitTextToSize(memo.trim(), 170), 15, finalY + 18);
      doc.setTextColor(0);
    }
    // Check payments: automatic clearance disclaimer, so a bounced check never
    // collides with a receipt that claimed cleared funds.
    if (showCheckNote && hasCheck) {
      const memoLineCount = (memo && memo.trim()) ? doc.splitTextToSize(memo.trim(), 170).length : 0;
      const noteY = finalY + (memoLineCount ? 18 + memoLineCount * 5 + 6 : 12);
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(9);
      doc.setTextColor(120);
      doc.text('Payment received by check — this receipt is issued subject to bank clearance of funds.', 15, noteY);
      doc.setTextColor(0);
      doc.setFont('helvetica', 'normal');
    }
    return doc;
  };

  const generatePDF = () => {
    buildPDF().save(`Receipt_${receiptNumber}.pdf`);
  };

  // Send: creates a DRAFT in your Outlook mailbox with the receipt PDF attached
  // (same flow as invoices/quotes — nothing is emailed until you review the
  // draft in Outlook and hit Send yourself).
  const handleSend = async () => {
    if (!selectedProject) return alert('Please select a project');
    // Fetch the LATEST customer at send time — the page's copy is a snapshot
    // from page load (or a restored draft) and goes stale if the customer was
    // edited since (name, email, etc.).
    let customer = billToCust || selectedProject?.customer || {};
    const custId = customer._id || (typeof customer === 'string' ? customer : null);
    if (custId) {
      try {
        const { data } = await axios.get(`${API_BASE}/customers/${custId}`, { headers: { Authorization: localStorage.getItem('token') } });
        if (data) customer = data;
      } catch (_) { /* fall back to the snapshot */ }
    }
    const email = customer.primaryEmail || customer.altEmail || '';
    const firstName = customer.firstName || (customer.name || '').trim().split(/\s+/)[0] || 'there';
    const hour = new Date().getHours();
    const timeOfDay = hour < 12 ? 'morning' : hour < 17 ? 'afternoon' : 'evening';

    const subject = `Payment Receipt ${receiptNumber} — Stalwart Electric`;
    // Check payments: one soft line in the email mirroring the receipt's
    // clearance disclaimer, so a bounced check contradicts nothing in writing.
    const checkNote = (showCheckNote && hasCheck)
      ? `Please note: as the payment was made by check, this receipt is issued subject to bank clearance. `
      : '';
    const body =
      `Good ${timeOfDay} ${firstName},\n\n` +
      `Attached is your payment receipt (# ${receiptNumber}) — thank you for your payment! ` +
      checkNote +
      `If you have any questions, please reach out.\n\n` +
      `Thank you,`;

    if (!email) alert('This customer has no email on file — the draft will open with an empty "To" field.');

    try {
      const token = localStorage.getItem('token');
      const pdfBase64 = buildPDF().output('datauristring');
      const filename = `Receipt_${receiptNumber.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`;
      await axios.post(
        `${API_BASE}/invoices/create-outlook-draft`,
        { to: email, subject, body, pdfBase64, filename },
        { headers: { Authorization: token } }
      );
      alert('Draft with PDF created in your Outlook Drafts — review & send.');
    } catch (err) {
      alert('Could not create the Outlook draft: ' + (err.response?.data?.error || err.message));
    }
  };

  if (loadingProjects) return <div className="min-h-screen bg-gray-950 flex items-center justify-center text-white">Loading Projects...</div>;

  return (
    <div className="create-receipt-page" style={{ minHeight: '100vh', backgroundColor: '#f6f9fc', padding: isMobile ? '16px 12px 32px' : '32px 40px', fontFamily: 'sans-serif', color: '#32325d', boxSizing: 'border-box', overflowX: 'hidden' }}>
      {/* Inputs use width:100% + padding — keep them inside their cards */}
      <style>{`.create-receipt-page input, .create-receipt-page select, .create-receipt-page textarea { box-sizing: border-box; max-width: 100%; }`}</style>
      
      {/* HEADER */}
      <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'stretch' : 'center', gap: isMobile ? '12px' : 0, marginBottom: isMobile ? '20px' : '40px' }}>
        <h1 style={{ margin: 0, fontSize: isMobile ? '22px' : '28px', fontWeight: 600 }}>Create Receipt</h1>
        <div style={isMobile ? { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' } : { display: 'flex', gap: '12px' }}>
          <button style={{ padding: '10px 20px', border: '1px solid #e6e6e6', borderRadius: '6px', background: 'white', cursor: 'pointer' }} onClick={() => navigate('/billing')}>Cancel</button>
          <button onClick={handleSave} disabled={!!savedNumber} title={savedNumber ? 'Already saved — use Send or Download' : undefined} style={{ padding: '10px 20px', background: savedNumber ? '#86efac' : '#10b981', color: 'white', border: 'none', borderRadius: '6px', fontWeight: 600, cursor: savedNumber ? 'default' : 'pointer' }}>{savedNumber ? 'Saved ✓' : 'Save Payment'}</button>
          <button onClick={generatePDF} style={{ padding: '10px 20px', background: '#ef4632', color: 'white', border: 'none', borderRadius: '6px', fontWeight: 600, cursor: 'pointer' }}>Download PDF</button>
          <button onClick={handleSend} disabled={!selectedProject} title={!selectedProject ? 'Select a project first' : 'Create an Outlook draft with the PDF attached'} style={{ padding: '10px 20px', background: !selectedProject ? '#a5b4fc' : '#635bff', color: 'white', border: 'none', borderRadius: '6px', fontWeight: 600, cursor: !selectedProject ? 'default' : 'pointer' }}>Send</button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'minmax(0, 1fr)' : 'minmax(0, 1fr) minmax(0, 1fr)', gap: isMobile ? '16px' : '32px' }}>
        
        {/* Left: Form */}
        <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', padding: isMobile ? '18px 16px' : '32px', minWidth: 0 }}>
          <section style={{ marginBottom: isMobile ? '22px' : '32px' }}>
            <h2 style={{ fontSize: '18px', fontWeight: 600, margin: '0 0 12px' }}>Display Options</h2>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '12px', padding: isMobile ? '12px' : '16px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px' }}>
                <input type="checkbox" checked={showInvoiceTotal} onChange={e => setShowInvoiceTotal(e.target.checked)} /> Show Invoice Total
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px' }}>
                <input type="checkbox" checked={showPrevPayments} onChange={e => setShowPrevPayments(e.target.checked)} /> Show Previous Payments
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px' }}>
                <input type="checkbox" checked={showRemainingBalance} onChange={e => setShowRemainingBalance(e.target.checked)} /> Show Balance Due
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px' }}>
                <input type="checkbox" checked={showCardFee} onChange={e => setShowCardFee(e.target.checked)} /> Show 3.5% Card Fee
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px' }}>
                <input type="checkbox" checked={showCheckNote} onChange={e => setShowCheckNote(e.target.checked)} /> Show Check-Clearance Note
              </label>
            </div>
          </section>

          <section style={{ marginBottom: isMobile ? '22px' : '32px' }}>
            <h2 style={{ fontSize: '18px', fontWeight: 600, margin: '0 0 12px' }}>Project Selection</h2>
            {/* Visible confirmation of the chosen project — before this, the
                search box just sat empty after selection (or ?project= prefill)
                and it looked like nothing was selected. */}
            {selectedProject && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', padding: '10px 14px', marginBottom: '8px', background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: '6px', fontSize: '14px' }}>
                <span style={{ minWidth: 0, overflowWrap: 'anywhere' }}><strong>PO {selectedProject.poNumber || '—'}</strong> — {selectedProject.customer?.name || 'Client'} ({selectedProject.title || 'Untitled'})</span>
                <button onClick={() => { setSelectedProject(null); setSelectedInvoice(null); }} style={{ border: 'none', background: 'none', color: '#6366f1', cursor: 'pointer', fontWeight: 600, flexShrink: 0 }}>Change</button>
              </div>
            )}
            {selectedProject && <BillToPicker project={selectedProject} value={billToCust} onChange={setBillToCust} />}
            <input
              type="text"
              placeholder="Search by PO# or Client..."
              value={projectSearch}
              onChange={(e) => setProjectSearch(e.target.value)}
              style={{ width: '100%', padding: '12px 16px', border: '1px solid #ccd0d5', borderRadius: '6px', fontSize: '16px' }}
            />
            {projectSearch && (
              <div style={{ marginTop: '8px', border: '1px solid #ccd0d5', borderRadius: '6px', maxHeight: '200px', overflowY: 'auto', background: 'white' }}>
                {projects.filter(p => p.poNumber?.toLowerCase().includes(projectSearch.toLowerCase()) || p.customer?.name?.toLowerCase().includes(projectSearch.toLowerCase())).map((p) => (
                  <div key={p._id} onClick={() => { setSelectedProject(p); setBillToCust(null); setProjectSearch(''); }} style={{ padding: '12px 16px', cursor: 'pointer', borderBottom: '1px solid #f0f0f0' }}>
                    <strong>{p.poNumber}</strong> - {p.customer?.name} ({p.title})
                  </div>
                ))}
              </div>
            )}
          </section>

          {selectedProject && (
            <>
              <section style={{ marginBottom: isMobile ? '22px' : '32px' }}>
                <h2 style={{ fontSize: '18px', fontWeight: 600, margin: '0 0 12px' }}>Select Invoice</h2>
                <select
                  value={selectedInvoice?._id || ''}
                  onChange={(e) => {
                    const inv = projectInvoices.find(i => i._id === e.target.value);
                    setSelectedInvoice(inv);
                    if (inv) setFirstRowAmount(inv.total);
                  }}
                  style={{ width: '100%', padding: '12px 16px', border: '1px solid #ccd0d5', borderRadius: '6px', fontSize: '16px' }}
                >
                  <option value="">{projectInvoices.length > 0 ? "Choose an invoice..." : "No invoices found for this project"}</option>
                  {projectInvoices.map(inv => (
                    <option key={inv._id} value={inv._id}>{inv.number} - (${formatPrice(inv.total)})</option>
                  ))}
                </select>
              </section>

              {showPrevPayments && (
                <section style={{ marginBottom: '32px', padding: '16px', border: '1px solid #ef4632', borderRadius: '8px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <h2 style={{ fontSize: '16px', fontWeight: 600, margin: 0 }}>Previous Payments</h2>
                    <button onClick={addPrevPayment} style={{ background: '#ef4632', color: 'white', border: 'none', borderRadius: '4px', padding: '4px 8px', fontSize: '12px', cursor: 'pointer' }}>+ Add Row</button>
                  </div>
                  {previousPayments.map((p, idx) => (
                    <div key={idx} style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center' }}>
                      <input placeholder="Date" value={p.date} onChange={e => updatePrevPayment(idx, 'date', e.target.value)} style={{ flex: 1, minWidth: 0, padding: '8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '16px' }} />
                      <input type="number" placeholder="Amount" value={p.amount} onChange={e => updatePrevPayment(idx, 'amount', e.target.value)} style={{ flex: 1, minWidth: 0, padding: '8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '16px' }} />
                      <button onClick={() => removePrevPayment(idx)} style={{ border: 'none', background: 'none', color: '#ccc', cursor: 'pointer', fontSize: '18px' }}>×</button>
                    </div>
                  ))}
                </section>
              )}

              <section style={{ marginBottom: isMobile ? '22px' : '32px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '0 0 12px' }}>
                  <h2 style={{ fontSize: '18px', fontWeight: 600, margin: 0 }}>Payments on this Receipt</h2>
                  <button onClick={addPayRow} style={{ background: '#635bff', color: 'white', border: 'none', borderRadius: '4px', padding: '5px 10px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>+ Split Payment</button>
                </div>
                {payRows.map((p, idx) => (
                  <div key={idx} style={{ display: 'flex', flexWrap: isMobile ? 'wrap' : 'nowrap', gap: '8px', marginBottom: '8px', alignItems: 'center' }}>
                    <input type="date" value={p.date} onChange={(e) => updatePayRow(idx, 'date', e.target.value)} style={{ flex: isMobile ? '1 1 46%' : 1.1, minWidth: 0, padding: '10px', border: '1px solid #ccd0d5', borderRadius: '6px', fontSize: '16px' }} />
                    <input type="number" placeholder="Amount" value={p.amount} onChange={(e) => updatePayRow(idx, 'amount', e.target.value)} style={{ flex: isMobile ? '1 1 46%' : 1, minWidth: 0, padding: '10px', border: '1px solid #ccd0d5', borderRadius: '6px', fontSize: '16px' }} />
                    <select
                      value={STD_METHODS.includes(p.method) ? p.method : 'Other'}
                      onChange={(e) => updatePayRow(idx, 'method', e.target.value === 'Other' ? '' : e.target.value)}
                      style={{ flex: isMobile ? '1 1 70%' : 1, minWidth: 0, padding: '10px', border: '1px solid #ccd0d5', borderRadius: '6px', fontSize: '16px' }}
                    >
                      {STD_METHODS.map(m => <option key={m}>{m}</option>)}
                      <option>Other</option>
                    </select>
                    {!STD_METHODS.includes(p.method) && (
                      <input
                        placeholder="e.g. Venmo, Cash App, Square, Apple Pay"
                        value={p.method}
                        onChange={(e) => updatePayRow(idx, 'method', e.target.value)}
                        style={{ flex: isMobile ? '1 1 100%' : 1.2, minWidth: 0, padding: '10px', border: '1px solid #ccd0d5', borderRadius: '6px', fontSize: '16px' }}
                      />
                    )}
                    {payRows.length > 1 && <button onClick={() => removePayRow(idx)} style={{ border: 'none', background: 'none', color: '#ccc', cursor: 'pointer', fontSize: '18px' }}>×</button>}
                  </div>
                ))}
                {payRows.length > 1 && (
                  <div style={{ textAlign: 'right', fontSize: '14px', fontWeight: 700, marginTop: '4px' }}>Total received: ${formatPrice(totalPaidNow)}</div>
                )}
              </section>
            </>
          )}

          <section>
            <h2 style={{ fontSize: '18px', fontWeight: 600, margin: '0 0 12px' }}>Memo</h2>
            <textarea value={memo} onChange={(e) => setMemo(e.target.value)} placeholder="Payment details..." style={{ width: '100%', height: '80px', padding: '12px 16px', border: '1px solid #ccd0d5', borderRadius: '6px', fontSize: '16px' }} />
          </section>
        </div>

        {/* Right: Preview */}
        <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.1)', padding: isMobile ? '14px' : '20px', position: isMobile ? 'static' : 'sticky', top: '20px', alignSelf: 'start', minWidth: 0 }}>
          <h2 style={{ fontSize: '18px', fontWeight: 600, margin: '0 0 16px' }}>Live Preview</h2>
          <div style={{ width: '100%', aspectRatio: isMobile ? 'auto' : '8.5 / 11', background: 'white', border: '1px solid #ddd', borderRadius: '8px', overflow: 'hidden', position: 'relative', boxSizing: 'border-box' }}>
            <div style={{ padding: isMobile ? '20px 16px' : '40px 48px', height: isMobile ? 'auto' : '100%', color: '#000', boxSizing: 'border-box' }}>
              <img src="/media/LogoXS.jpg" alt="Logo" style={{ width: isMobile ? '40px' : '50px', position: 'absolute', top: isMobile ? '16px' : '30px', right: isMobile ? '16px' : '48px' }} />
              <h3 style={{ margin: 0, fontSize: isMobile ? '20px' : '24px', fontWeight: 700 }}>RECEIPT</h3>
              <div style={{ fontSize: '12px', marginTop: '10px', paddingRight: isMobile ? '48px' : 0, wordBreak: 'break-word' }}>Receipt # {receiptNumber}<br />Date: {new Date().toLocaleDateString()}</div>
              
              <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? '14px' : 0, justifyContent: 'space-between', fontSize: '12px', marginTop: isMobile ? '20px' : '30px', marginBottom: isMobile ? '20px' : '30px' }}>
                <div style={{ width: isMobile ? '100%' : '45%' }}><strong style={{ fontSize: '14px' }}>STALWART ELECTRIC</strong><br />4712 Solano Way<br />Fair Oaks, CA 95628<br />916-860-4198<br />CSLB Lic. #1109509 · C-10</div>
                <div style={{ width: isMobile ? '100%' : '45%', wordBreak: 'break-word' }}>
                  <strong style={{ fontSize: '14px' }}>RECEIVED FROM:</strong><br />
                  {(() => { const pc = billToCust || selectedProject?.customer || {}; return pc.businessName && selectedProject?.billTo !== 'Individual' ? (<>{pc.businessName}<br />Attn: {pc.name}</>) : (pc.name || 'Client Name'); })()}
                  {(() => { const pc = billToCust || selectedProject?.customer || {}; const a = billingAddressFor(pc, orgAddrs); return (<>
                    {a.street && <><br />{a.street}</>}
                    {(a.city || a.zip) && <><br />{[a.city, a.state, a.zip].filter(Boolean).join(', ')}</>}
                    {pc.primaryPhone && <><br />{pc.primaryPhone}</>}
                  </>); })()}
                  <br />Project: {selectedProject?.title || '---'}
                </div>
              </div>

              <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: isMobile ? '20px' : '32px', fontSize: '12px', tableLayout: 'fixed', wordBreak: 'break-word' }}>
                <thead><tr style={{ background: '#ef4632', color: 'white' }}><th style={{ textAlign: 'left', padding: '10px' }}>Description</th><th style={{ textAlign: 'right', padding: '10px', width: isMobile ? '38%' : '30%' }}>Amount</th></tr></thead>
                <tbody>
                  {showInvoiceTotal && <tr style={{ borderBottom: '1px solid #eee' }}><td style={{ padding: '10px' }}>Original Invoice Total ({selectedInvoice?.number || '---'})</td><td style={{ textAlign: 'right', padding: '10px' }}>${formatPrice(invoiceTotal)}</td></tr>}
                  {showPrevPayments && previousPayments.map((p, i) => <tr key={i} style={{ borderBottom: '1px solid #eee', color: '#666' }}><td style={{ padding: '10px' }}>Payment Received ({p.date || '---'})</td><td style={{ textAlign: 'right', padding: '10px' }}>-${formatPrice(p.amount)}</td></tr>)}
                  {(payRows.filter(p => Number(p.amount) > 0).length ? payRows.filter(p => Number(p.amount) > 0) : [payRows[0]]).map((p, i, arr) => (
                    <tr key={`pay-${i}`} style={{ borderBottom: i === arr.length - 1 ? '2px solid #000' : '1px solid #eee' }}>
                      <td style={{ padding: '12px 10px' }}>
                        <strong>{arr.length > 1 ? `PAYMENT RECEIVED (${p.date})` : 'CURRENT PAYMENT'}</strong> for {selectedInvoice?.number || 'Project'}<br />
                        <span style={{ color: '#666', fontSize: '10px' }}>Method: {p.method}</span>
                      </td>
                      <td style={{ textAlign: 'right', padding: '12px 10px', fontWeight: 700, fontSize: '14px' }}>${formatPrice(p.amount)}</td>
                    </tr>
                  ))}
                  {showCardFee && cardFee > 0 && (
                    <tr style={{ borderBottom: '1px solid #eee', color: '#666' }}>
                      <td style={{ padding: '10px' }}>Card processing fee (3.5%)</td>
                      <td style={{ textAlign: 'right', padding: '10px' }}>${formatPrice(cardFee)}</td>
                    </tr>
                  )}
                </tbody>
              </table>

              <div style={{ marginLeft: 'auto', width: isMobile ? '100%' : '55%', textAlign: 'right', borderTop: '4px solid #ef4632', paddingTop: '10px' }}>
                {showCardFee && cardFee > 0 && (
                  <div style={{ fontSize: '13px', color: '#555', marginBottom: '6px' }}>Total charged (incl. card fee): <strong>${formatPrice(totalCharged)}</strong></div>
                )}
                {showRemainingBalance ? (
                  <>
                    <span style={{ fontSize: '11px', fontWeight: 700, color: '#a0aec0', textTransform: 'uppercase' }}>Total Balance Due</span>
                    <div style={{ fontSize: '22px', fontWeight: 900, color: '#ef4632' }}>${formatPrice(remainingBalance)}</div>
                  </>
                ) : (
                  <div style={{ fontSize: '18px', fontWeight: 700 }}>Total Received: ${formatPrice(totalPaidNow)}</div>
                )}
              </div>
              {/* Memo (matches the PDF) */}
              {memo && memo.trim() && (
                <div style={{ marginTop: '18px', fontSize: '12px' }}>
                  <div style={{ fontWeight: 700, marginBottom: '4px' }}>Memo</div>
                  <div style={{ color: '#555', whiteSpace: 'pre-wrap' }}>{memo.trim()}</div>
                </div>
              )}
              {/* Check payments: clearance disclaimer (matches the PDF) */}
              {showCheckNote && hasCheck && (
                <div style={{ marginTop: '12px', fontSize: '11px', fontStyle: 'italic', color: '#888' }}>
                  Payment received by check — this receipt is issued subject to bank clearance of funds.
                </div>
              )}
              <div style={isMobile ? { marginTop: '24px', fontSize: '13px', color: '#444' } : { position: 'absolute', bottom: '40px', left: '48px', right: '48px', fontSize: '13px', color: '#444' }}><p>Thank you for choosing Stalwart Electric!</p></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CreateReceipt;